import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Download, 
  File, 
  FileText, 
  Image, 
  FileSpreadsheet,
  Loader2, 
  Search,
  User,
  Calendar,
  Package,
  Link,
  ExternalLink
} from 'lucide-react';
import { downloadFileById } from '@/lib/file-download';
import { supabase } from '@/integrations/supabase/client';

interface SubmissionWithFiles {
  submission_id: string;
  lead_id: string;
  product_slug: string;
  product_name: string;
  product_group: string;
  submitted_at: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  files: {
    upload_id: string;
    file_path: string;
    doc_type: string;
    mime_type: string;
    size_bytes: number;
    uploaded_at: string;
  }[];
}

export function SubmissionFileTracker() {
  const [submissions, setSubmissions] = useState<SubmissionWithFiles[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());

  // Load submissions with their files
  const loadSubmissionsWithFiles = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('submissions')
        .select(`
          id,
          lead_id,
          submitted_at,
          products!inner(slug, name, group),
          leads!inner(
            id,
            lead_contacts(name, email, phone)
          ),
          uploads(
            id,
            file_path,
            doc_type,
            mime_type,
            size_bytes,
            uploaded_at
          )
        `)
        .order('submitted_at', { ascending: false });

      if (error) throw error;

      // Transform the data
      const transformedData: SubmissionWithFiles[] = data?.map(submission => ({
        submission_id: submission.id,
        lead_id: submission.lead_id || '',
        product_slug: submission.products?.slug || '',
        product_name: submission.products?.name || '',
        product_group: submission.products?.group || '',
        submitted_at: submission.submitted_at || '',
        contact_name: submission.leads?.lead_contacts?.[0]?.name || '',
        contact_email: submission.leads?.lead_contacts?.[0]?.email || '',
        contact_phone: submission.leads?.lead_contacts?.[0]?.phone || '',
        files: submission.uploads?.map(upload => ({
          upload_id: upload.id,
          file_path: upload.file_path,
          doc_type: upload.doc_type || '',
          mime_type: upload.mime_type || '',
          size_bytes: upload.size_bytes || 0,
          uploaded_at: upload.uploaded_at || ''
        })) || []
      })) || [];

      setSubmissions(transformedData);
    } catch (error) {
      console.error('Error loading submissions with files:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubmissionsWithFiles();
  }, []);

  // Handle file download
  const handleFileDownload = async (uploadId: string, fileName: string) => {
    setDownloadingFiles(prev => new Set(prev).add(uploadId));
    
    try {
      const result = await downloadFileById(uploadId);
      if (!result.success) {
        console.error('Download failed:', result.error);
      }
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(uploadId);
        return newSet;
      });
    }
  };

  // Get file icon based on mime type
  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return <Image className="h-4 w-4 text-blue-500" />;
    } else if (mimeType.includes('pdf')) {
      return <FileText className="h-4 w-4 text-red-500" />;
    } else if (mimeType.includes('sheet') || mimeType.includes('excel')) {
      return <FileSpreadsheet className="h-4 w-4 text-green-500" />;
    } else {
      return <File className="h-4 w-4 text-gray-500" />;
    }
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  // Filter submissions based on search
  const filteredSubmissions = submissions.filter(submission => 
    submission.contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    submission.contact_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    submission.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    submission.files.some(file => 
      file.doc_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.file_path.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <Card className="w-full max-w-7xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link className="h-5 w-5" />
          Submission & File Tracker
        </CardTitle>
        <CardDescription>
          View the complete relationship between submissions, customers, and their uploaded files
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Search and Controls */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Label htmlFor="search" className="sr-only">Search submissions</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="search"
                placeholder="Search by customer name, email, product, or file type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Button onClick={loadSubmissionsWithFiles} disabled={loading} variant="outline">
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              'Refresh'
            )}
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-blue-900">Total Submissions</h3>
            <p className="text-2xl font-bold text-blue-700">{submissions.length}</p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <h3 className="font-medium text-green-900">With Files</h3>
            <p className="text-2xl font-bold text-green-700">
              {submissions.filter(s => s.files.length > 0).length}
            </p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <h3 className="font-medium text-purple-900">Total Files</h3>
            <p className="text-2xl font-bold text-purple-700">
              {submissions.reduce((sum, s) => sum + s.files.length, 0)}
            </p>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg">
            <h3 className="font-medium text-orange-900">Filtered Results</h3>
            <p className="text-2xl font-bold text-orange-700">{filteredSubmissions.length}</p>
          </div>
        </div>

        {/* Submissions List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
              <p>Loading submissions and files...</p>
            </div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? 'No submissions match your search criteria' : 'No submissions found'}
            </div>
          ) : (
            filteredSubmissions.map((submission) => (
              <Card key={submission.submission_id} className="border-l-4 border-l-blue-500">
                <CardContent className="p-6">
                  {/* Submission Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <User className="h-5 w-5 text-gray-500" />
                        <h3 className="font-semibold text-lg">{submission.contact_name}</h3>
                        <Badge variant="outline">{submission.product_group}</Badge>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                        <div>
                          <p><strong>Email:</strong> {submission.contact_email}</p>
                          <p><strong>Phone:</strong> {submission.contact_phone}</p>
                        </div>
                        <div>
                          <p><strong>Product:</strong> {submission.product_name}</p>
                          <p><strong>Submitted:</strong> {formatDate(submission.submitted_at)}</p>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="secondary" className="mb-2">
                        ID: {submission.submission_id.slice(0, 8)}
                      </Badge>
                      <p className="text-xs text-gray-500">
                        {submission.files.length} file{submission.files.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>

                  {/* Files Section */}
                  {submission.files.length > 0 ? (
                    <div className="border-t pt-4">
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Uploaded Files ({submission.files.length})
                      </h4>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {submission.files.map((file) => (
                          <div key={file.upload_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {getFileIcon(file.mime_type)}
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">
                                  {file.doc_type || 'Unknown Document'}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {file.file_path}
                                </p>
                                <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                                  <span>{formatFileSize(file.size_bytes)}</span>
                                  <span>{formatDate(file.uploaded_at)}</span>
                                </div>
                              </div>
                            </div>
                            <Button
                              onClick={() => handleFileDownload(file.upload_id, file.doc_type)}
                              disabled={downloadingFiles.has(file.upload_id)}
                              size="sm"
                              variant="outline"
                            >
                              {downloadingFiles.has(file.upload_id) ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="border-t pt-4 text-center text-gray-500">
                      <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No files uploaded for this submission</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">🔗 Understanding File Relationships:</h4>
          <div className="text-sm text-blue-800 space-y-1">
            <p>• <strong>Submission ID:</strong> Unique identifier linking customer form submission to files</p>
            <p>• <strong>File Path:</strong> Exact location in Supabase storage bucket</p>
            <p>• <strong>Document Type:</strong> Category of document (trade_license, fleet_file, etc.)</p>
            <p>• <strong>Customer Link:</strong> Each file is connected to a specific customer and product</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
